import { applyMask, stripToDigits } from '@noor/lib';

/**
 * Phone formatting for the legacy order form. Pure, so the mask rules can be
 * tested without mounting an input.
 *
 * The CANONICAL form is E.164 (`+998901234567`) — that is what goes on the
 * wire. The mask is presentation only.
 */

/** Digit slots after the `+998` country code, from the old country table. */
export const LEGACY_PHONE_MASK = '(##) ### ## ##';
export const LEGACY_PHONE_DIGITS = 9;
const COUNTRY_CODE = '998';

/**
 * The national part of a stored value — `+998901234567` → `901234567`.
 *
 * Tolerates input with or without the country code, and with or without
 * formatting, because a value can arrive from the wire, from a paste, or from
 * a partially typed field.
 */
export function toNationalDigits(value: string): string {
  const digits = stripToDigits(value);
  // Strip `998` only when it is the country code, not part of the national
  // number. A bare digit string of ≤9 chars is already national — e.g. typing
  // `998...` into the masked field (national numbers can start with `998`).
  // Longer strings (paste/E.164 with the code) still strip, as does any value
  // carrying an explicit `+` (e.g. `+998` → ``).
  const isCountryCode =
    digits.startsWith(COUNTRY_CODE) &&
    (digits.length > LEGACY_PHONE_DIGITS || value.includes('+'));
  const withoutCode = isCountryCode ? digits.slice(COUNTRY_CODE.length) : digits;
  return withoutCode.slice(0, LEGACY_PHONE_DIGITS);
}

/** National digits rendered through the mask, for display. */
export function formatNationalPhone(value: string): string {
  return applyMask(toNationalDigits(value), LEGACY_PHONE_MASK);
}

/**
 * Raw input → the canonical value to store. Empty input yields `''`, NOT a
 * bare `+998`: a country code with no subscriber number is not a phone number,
 * and storing one would put an unreachable contact on the order.
 */
export function toCanonicalPhone(raw: string): string {
  const digits = toNationalDigits(raw);
  return digits ? `+${COUNTRY_CODE}${digits}` : '';
}

/** A phone is usable once every national digit is present. */
export function isCompletePhone(value: string): boolean {
  return toNationalDigits(value).length === LEGACY_PHONE_DIGITS;
}
